/**
 * Forum Component
 *
 * Features:
 * - Displays the forum section for a lesson, including user comment input and all comments.
 * - Fetches and displays comments for the current lesson.
 * - Shows loading indicator while comments are being fetched.
 * - Passes relevant props to child components for comment data and actions.
 * - Uses Material-UI and CSS modules for styling.
 *
 * @param {Object} props - Component properties
 * @param {number} [props.idLesson] - Lesson ID for which to display the forum
 * @param {number} [props.idCourse] - Course ID for which to display the forum
 * @returns {JSX.Element} Rendered Forum component
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getCommentsForum } from 'store/lesson-actions';

import CommentContainer from './CommentContainer/CommentContainer';
import UserComment from './UserComment/UserComment';
import Loading from 'components/UI/Loading/Loading';

import classes from './Forum.module.css';

const Forum = props => {
  const { t } = useTranslation(['forum']);
  const dispatch = useDispatch();
  const dataLesson = useSelector(state => state.lessonInfo);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    dispatch(getCommentsForum(props.idLesson, 'es', 50, 1, true));
  }, []);

  useEffect(() => {
    if (dataLesson.commentsForum !== null) {
      setIsLoading(false);
    }
  }, [dataLesson]);

  return (
    <section className={classes.forumContainer}>
      {/* Custom component: UserComment receives idLesson, idCourse */}
      {!isLoading ? (
        <>
          <UserComment
            idLesson={props.idLesson}
            idCourse={props.idCourse}
          />
          <aside className={classes.commentsForum}>
            <p className={classes.totalComments}>
              {dataLesson.commentsForum.paginator.items}{' '}
              {t('comments')}
            </p>
            {/* Custom component: CommentContainer receives commentData, idLesson, idCourse */}
            {dataLesson.commentsForum.comments.map(comment => (
              <CommentContainer
                key={comment.idComment}
                commentData={comment}
                idLesson={props.idLesson}
                idCourse={props.idCourse}
              />
            ))}
          </aside>
        </>
      ) : (
        <Loading />
      )}
    </section>
  );
};

/**
 * PropTypes definition for type checking and development documentation
 */
Forum.propTypes = {
  /** Lesson ID for which to display the forum */
  idLesson: PropTypes.number,
  /** Course ID for which to display the forum */
  idCourse: PropTypes.number
};

Forum.defaultProps = {
  idLesson: undefined,
  idCourse: undefined
};

export default Forum;
